import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import React, { FC, useMemo, useState, useRef } from "react";
import { SpecialChartResponseType } from "../../types/specialChartType";
import { barColors } from "./barColors";
import { I18n } from "react-redux-i18n";
import { stripMarkdown, transformData } from "./helpers";

type ChartComponentProps = {
  allData: SpecialChartResponseType;
};

const StackedBarChart: FC<ChartComponentProps> = ({ allData }) => {
  const [tooltipContent, setTooltipContent] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeout = useRef<NodeJS.Timeout | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const answerValues = useMemo(() => Array.from(new Set(allData.map((item) => item.value))), [allData]);

  const transformedData = useMemo(() => {
    const data = transformData(allData);

    return data.filter((item) => item.name !== "0");
  }, [allData]);

  const getColor = (index: number) => {
    const colors = barColors;
    return colors[index % colors.length];
  };

  const handleLegendMouseEnter = (e: React.MouseEvent, value: string) => {
    if (tooltipTimeout.current) {
      clearTimeout(tooltipTimeout.current);
    }
    tooltipTimeout.current = setTimeout(() => {
      const fullText = allData.find((item) => item.value === value)?.value || value;
      setTooltipContent(fullText);
      setTooltipPosition({ x: e.clientX, y: e.clientY });
      setShowTooltip(true);
    }, 500);
  };

  const handleLegendMouseLeave = () => {
    if (tooltipTimeout.current) {
      clearTimeout(tooltipTimeout.current);
    }
    setShowTooltip(false);
  };

  const truncateText = (text: string, maxLength: number) =>
    text.length > maxLength ? text.substring(0, maxLength) + "..." : text;

  const renderCustomizedLegend = (props: any) => {
    const { payload } = props;

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", padding: "10px" }}>
        {payload.map((entry: any, index: number) => (
          <div
            key={`legend-item-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 8,
              cursor: "pointer",
              width: "100%",
            }}
            onMouseEnter={(e) => handleLegendMouseEnter(e, entry.value)}
            onMouseLeave={handleLegendMouseLeave}
          >
            <div style={{ width: 16, height: 16, backgroundColor: entry.color, marginRight: 8 }} />
            <span style={{ fontSize: "0.8rem" }}>{truncateText(stripMarkdown(entry.value), 40)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ width: "100%", position: "relative" }}>
      <div style={{ width: "100%", height: 400 }}>
        <ResponsiveContainer>
          <BarChart
            data={transformedData}
            margin={{
              top: 20,
              right: 30,
              left: 100,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis
              label={{ value: I18n.t("numberOfAnswers"), angle: -90, position: "insideLeft", dy: 60, fontSize: 18 }}
              allowDecimals={false}
            />
            <Tooltip
              wrapperStyle={{ maxWidth: "300px" }}
              contentStyle={{ maxWidth: "500px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              formatter={(value, name: string) => [value, stripMarkdown(name)]}
            />
            <Legend
              content={renderCustomizedLegend}
              layout="vertical"
              align="left"
              verticalAlign="middle"
              wrapperStyle={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)" }}
            />
            {answerValues.map((value, index) => (
              <Bar
                key={value}
                dataKey={value}
                name={stripMarkdown(value)}
                stackId="a"
                fill={getColor(index)}
                barSize={35}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {showTooltip && tooltipContent && (
        <div
          ref={tooltipRef}
          style={{
            position: "fixed",
            top: tooltipPosition.y + 10,
            left: tooltipPosition.x + 10,
            background: "rgba(255, 255, 255, 0.95)",
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "8px 12px",
            maxWidth: "400px",
            zIndex: 1000,
            boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
            fontSize: "0.8rem",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
            opacity: showTooltip ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          }}
          onMouseLeave={handleLegendMouseLeave}
        >
          {tooltipContent}
        </div>
      )}
    </div>
  );
};

export default StackedBarChart;
